import { Button, Select, Table, Tag, message } from "antd";
import axios from "axios";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { serverUrl } from "../../../nestserver";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});


const GroupDetails = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [schoolToAdd, setSchoolToAdd] = useState(null);
  const [adminToPromote, setAdminToPromote] = useState(null);

  const group = useQuery(["school-group", id], async () => {
    const res = await axios.get(`${serverUrl}/school-group/${id}`, {
      headers: authHeader(),
    });
    return res.data;
  });

  // Every school, so we can offer the standalone ones for assignment.
  const allSchools = useQuery("all-schools", async () => {
    const res = await axios.get(`${serverUrl}/school/`, {
      headers: authHeader(),
    });
    return res.data;
  });

  const promotable = useQuery(["group-promotable-admins", id], async () => {
    const res = await axios.get(
      `${serverUrl}/school-group/${id}/promotable-admins`,
      { headers: authHeader() }
    );
    return res.data;
  });

  const data = group?.data?.result?.data;
  const branches = data?.schools || [];
  const groupAdmins = data?.admins || [];
  const branchIds = new Set(branches.map((b) => b._id));

  const refresh = () => {
    queryClient.invalidateQueries(["school-group", id]);
    queryClient.invalidateQueries(["group-promotable-admins", id]);
    queryClient.invalidateQueries("all-schools");
    queryClient.invalidateQueries("school-groups");
  };

  const mutate = async (path, payload, successFallback) => {
    try {
      const res = await axios.patch(
        `${serverUrl}/school-group/${id}/${path}`,
        payload,
        { headers: authHeader() }
      );
      refresh();
      message.success(res?.data?.result?.message || successFallback);
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong"
      );
    }
  };

  const assignableSchools = (allSchools?.data?.result?.data || []).filter(
    (s) => !branchIds.has(s._id)
  );

  const branchColumns = [
    {
      title: "School Id",
      key: "refId",
      align: "center",
      render: (r) => <div className="text-center">{r?.refId}</div>,
    },
    {
      title: "Name",
      key: "name",
      align: "center",
      render: (r) => (
        <div className="text-center capitalize font-semibold">{r?.name}</div>
      ),
    },
    {
      title: "City",
      key: "city",
      align: "center",
      render: (r) => <div className="text-center capitalize">{r?.city}</div>,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (r) => (
        <div
          className="text-center text-red-500 cursor-pointer"
          onClick={() =>
            mutate(
              "schools",
              { schools: [r._id], assign: false },
              "Branch removed"
            )
          }
        >
          Remove
        </div>
      ),
    },
  ];

  const adminColumns = [
    {
      title: "Username",
      key: "username",
      align: "center",
      render: (r) => <div className="text-center">{r?.username}</div>,
    },
    {
      title: "Email",
      key: "email",
      align: "center",
      render: (r) => <div className="text-center">{r?.email}</div>,
    },
    {
      title: "Home Branch",
      key: "home",
      align: "center",
      render: (r) => (
        <div className="text-center">
          <Tag>{r?.home_school?.name || "—"}</Tag>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (r) => (
        <div
          className="text-center text-red-500 cursor-pointer"
          onClick={() =>
            mutate(
              "admins",
              { admins: [r._id], promote: false },
              "Admin removed from group"
            )
          }
        >
          Remove
        </div>
      ),
    },
  ];

  const card = "bg-white drop-shadow-md p-8 w-full mb-20";

  return (
    <div className="p-8" style={{ color: "#616161" }}>
      <div style={{ borderRadius: "10px", marginBottom: "1rem" }} className={card}>
        <div className="text-xl font-bold text-black">
          {data?.name}{" "}
          <span className="text-base font-normal text-gray-400">
            {data?.refId}
          </span>
        </div>
        <div className="text-sm text-gray-500 pt-1">
          Branches keep their own fees, classes and calendar. A group
          admin&apos;s School Posts reach every branch automatically.
        </div>
      </div>

      <div style={{ borderRadius: "10px", marginBottom: "1rem" }} className={card}>
        <div className="text-lg font-bold text-black pb-4">Branches</div>
        <div className="flex items-center gap-3 pb-4">
          <Select
            showSearch
            className="w-96"
            placeholder="Add an existing school as a branch"
            optionFilterProp="label"
            value={schoolToAdd}
            onChange={setSchoolToAdd}
            loading={allSchools?.isLoading}
            options={assignableSchools.map((s) => ({
              value: s._id,
              label: `${s.name} (${s.refId})`,
            }))}
          />
          <Button
            type="primary"
            disabled={!schoolToAdd}
            onClick={() => {
              mutate(
                "schools",
                { schools: [schoolToAdd], assign: true },
                "Branch added"
              );
              setSchoolToAdd(null);
            }}
          >
            Add Branch
          </Button>
        </div>
        <Table
          rowKey="_id"
          columns={branchColumns}
          dataSource={branches}
          pagination={false}
          loading={group?.isLoading}
        />
      </div>

      <div style={{ borderRadius: "10px", marginBottom: "1rem" }} className={card}>
        <div className="text-lg font-bold text-black pb-1">Group Admins</div>
        <div className="text-sm text-gray-500 pb-4">
          Promoted from an existing branch admin. They keep their own login and
          home branch, and gain a branch switcher in the header.
        </div>
        <div className="flex items-center gap-3 pb-4">
          <Select
            showSearch
            className="w-96"
            placeholder="Promote an existing branch admin"
            optionFilterProp="label"
            value={adminToPromote}
            onChange={setAdminToPromote}
            loading={promotable?.isLoading}
            options={(promotable?.data?.result?.data || []).map((a) => ({
              value: a._id,
              label: `${a.email || a.username} — ${a.school?.name || ""}`,
            }))}
          />
          <Button
            type="primary"
            disabled={!adminToPromote}
            onClick={() => {
              mutate(
                "admins",
                { admins: [adminToPromote], promote: true },
                "Admin promoted"
              );
              setAdminToPromote(null);
            }}
          >
            Promote
          </Button>
        </div>
        <Table
          rowKey="_id"
          columns={adminColumns}
          dataSource={groupAdmins}
          pagination={false}
          loading={group?.isLoading}
        />
      </div>
    </div>
  );
};

export default GroupDetails;
